import {DownloadIcon} from '@heroicons/react/outline';
import type {JSX} from 'react';
import {useState} from 'react';

import {buildDownloadFileName} from '@afriex/core/utils';

import {Button} from '@/components';
import {saveBlob} from '@/utils/downloadFile';
import {getErrorMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';
import {showErrorMessage} from '@/utils/showErrorMessage';

interface DownloadStatementButtonProps {
  /** Fetches the statement PDF the server builds for the range on screen. */
  download: () => Promise<Blob>;
  /** The `<what>` part of the saved name; see `buildDownloadFileName`. */
  fileName: string;
  /** Whose statement it is, the `<scope>` part of the saved name. */
  scope?: string;
  disabled?: boolean;
}

/**
 * Downloads an account statement. The server builds the whole PDF from the
 * range's transactions and streams it back, so the file covers every row, not
 * just the preview on screen (which the browser used to rasterise into the
 * PDF, capped at its first 100 rows).
 */
const DownloadStatementButton = ({
  download,
  fileName,
  scope,
  disabled = false,
}: DownloadStatementButtonProps): JSX.Element => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      saveBlob(await download(), buildDownloadFileName(fileName, 'pdf', scope));
    } catch (error) {
      showErrorMessage(
        getErrorMessage(error, 'Failed to download the statement'),
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      leftIcon={<DownloadIcon className="h-5 w-5" />}
      disabled={disabled || isDownloading}
      isLoading={isDownloading}
      loadingText="Preparing"
      onClick={() => {
        // handleDownload reports its own failures, so this only fires if that
        // reporting itself throws.
        handleDownload().catch(
          logRejection('Failed to download the statement'),
        );
      }}
    >
      Download
    </Button>
  );
};

export default DownloadStatementButton;
